import { useAuth0 } from "@auth0/auth0-react";
// Import Capacitor's app and browser plugins, giving us access to `addListener` and `appUrlOpen`,
// as well as the bits needed for Auth0 and React
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import {
	QueryClient,
	QueryClientProvider,
	useQueryClient,
} from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { useEffect, useRef } from "react";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
	useNavigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./auth/AuthProvider";
import { SentryUserProvider } from "./components/analytics/SentryProvider";
import { EraStatsigProvider } from "./components/analytics/StatsigProvider";
import { BottomNav } from "./components/BottomNav/BottomNav";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LanguageProvider } from "./components/i18n/LanguageProvider";
import {
	GlobalLoadingProvider,
	useGlobalLoading,
} from "./components/loading/global-loading-context";
import { GlobalLoadingModal } from "./components/loading/global-loading-modal";
import { PrimeReactConfigProvider } from "./components/providers/PrimeReactProvider";
import { SessionGuard } from "./components/SessionGuard";
import { SessionTimeoutHandler } from "./components/session/SessionTimeoutHandler";
import { UserDataLoader } from "./components/UserDataLoader";
import { CustomAblyProvider } from "./contexts/ably-provider";
import { ChatActionsProvider } from "./contexts/chat-actions-context";
import { DemoSettingsProvider } from "./contexts/demo-settings-context";
import {
	MobileAuthProvider,
	useMobileAuth,
} from "./contexts/mobile-auth-context";
import { ModalProvider } from "./contexts/modal-context";
import { UserDataProvider } from "./contexts/user-data-context";
import { useAnalytics } from "./lib/analytics";
import {
	setApiLoadingHandler,
	setApiTokenProvider,
	setApiUnauthorizedHandler,
} from "./lib/api";
import { performLogout } from "./lib/auth-logout";
import { Callback } from "./pages/Callback";
import { ChatHistory } from "./pages/ChatHistory";
import { ChatPage } from "./pages/ChatPage";
import { ChatWithProvider } from "./pages/ChatWithProvider";
import { Communication } from "./pages/Communication";
import { ConsultExpert } from "./pages/ConsultExpert";
import { Documents } from "./pages/Documents";
import { EducationHub } from "./pages/EducationHub";
import { FilesPage } from "./pages/FilesPage";
import { GetReadyPage } from "./pages/GetReadyPage";
import { HomePage } from "./pages/HomePage";
import { InvitationAccept } from "./pages/InvitationAccept";
import { Journey } from "./pages/Journey";
import { Login } from "./pages/Login";
import { Onboarding } from "./pages/Onboarding";
import { Pricing } from "./pages/Pricing";
import { ProfilePage } from "./pages/ProfilePage";
import { QuestionnairePage } from "./pages/QuestionnairePage";
import { SecuritySettings } from "./pages/SecuritySettings";
import { SettingsPage } from "./pages/SettingsPage";
import { SharePage } from "./pages/SharePage";
import { SubscriptionSettings } from "./pages/SubscriptionSettings";
import { SupportSettings } from "./pages/SupportSettings";
import { ThankYou } from "./pages/ThankYou";
import { TrackPage } from "./pages/TrackPage";
import { TwoFactor } from "./pages/TwoFactor";
import { BookAppointmentPage } from "./pages/tasks/BookAppointmentPage";
import { SignConsentPage } from "./pages/tasks/SignConsentPage";
import { UploadDocumentsPage } from "./pages/tasks/UploadDocumentsPage";
import * as RevenueCatService from "./services/revenueCatService";

const queryClient = new QueryClient();

// Mobile callback handler component that needs to be inside AuthProvider
function MobileCallbackHandler() {
	// Get the callback handler from the Auth0 React hook
	// Use a ref to always have the latest handleRedirectCallback without re-running the effect
	const { handleRedirectCallback } = useAuth0();
	const handleRedirectCallbackRef = useRef(handleRedirectCallback);
	handleRedirectCallbackRef.current = handleRedirectCallback;

	const navigate = useNavigate();
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;

	const { setMobileAuthReady } = useMobileAuth();
	const analytics = useAnalytics();
	const analyticsRef = useRef(analytics);
	analyticsRef.current = analytics;

	useEffect(() => {
		console.log("📱 Setting up appUrlOpen listener for deep links");
		// Handle the 'appUrlOpen' event and call `handleRedirectCallback`
		// This effect should only run ONCE on mount to avoid recreating the listener
		const setupListener = async () => {
			const listener = await CapApp.addListener(
				"appUrlOpen",
				async ({ url }) => {
					console.log("🔗 Deep link received:", url);

					// Track deep link event
					analyticsRef.current.logUserAction("deep_link_received", {
						platform: Capacitor.getPlatform(),
						url_path: new URL(url).pathname,
						has_auth_code: String(new URL(url).searchParams.has("code")),
					});

					// Parse URL to check for Auth0 callback parameters
					try {
						const urlObj = new URL(url);
						const hasCode = urlObj.searchParams.has("code");
						const hasState = urlObj.searchParams.has("state");
						const hasError = urlObj.searchParams.has("error");

						// Check if this is an Auth0 login callback (has code/error + state)
						if ((hasCode || hasError) && hasState) {
							console.log("✅ Detected Auth0 login callback URL");
							try {
								console.log("🔄 Calling handleRedirectCallback...");
								// For mobile deep links, call handleRedirectCallback with the exact URL
								// This processes the authorization code and completes the Auth0 flow
								// Use ref to get the latest callback without recreating the listener
								await handleRedirectCallbackRef.current(url);
								console.log("✅ handleRedirectCallback completed successfully");

								// Navigate to callback for proper JWT claims processing
								console.log("🔄 Navigating to /callback");
								navigateRef.current("/callback");
							} catch (error) {
								console.error("❌ Error handling Auth0 callback:", error);
							}
						} else if (url.includes("/callback")) {
							// Logout callback: Auth0 redirects back after logout but without code/state
							// This completes the logout flow - navigate to login page
							console.log(
								"🚪 Detected Auth0 logout callback, navigating to /login",
							);
							navigateRef.current("/login", { replace: true });
						} else {
							console.log("ℹ️ Not an Auth0 callback URL, ignoring");
						}
					} catch (error) {
						console.error("❌ Error parsing deep link URL:", error);
					}

					// Close the browser (no-op on Android)
					try {
						console.log("🔄 Attempting to close browser...");
						await Browser.close();
						console.log("✅ Browser closed successfully");
					} catch (error) {
						console.log("ℹ️ Browser close failed (expected on Android):", error);
						// Expected on Android - browser close is a no-op
					}
				},
			);

			// Signal that mobile auth is ready AFTER listener is registered
			// This prevents the Login page from triggering auth before we can handle callbacks
			setMobileAuthReady(true);
			console.log("✅ Mobile auth ready - appUrlOpen listener registered");

			// Store listener for cleanup
			return listener;
		};

		let listenerCleanup: { remove: () => void } | undefined;
		setupListener().then((listener) => {
			listenerCleanup = listener;
		});

		// Cleanup listener on unmount
		return () => {
			if (listenerCleanup) {
				listenerCleanup.remove();
			}
		};
		// Only setMobileAuthReady in deps - it's stable from useState
		// We use refs for handleRedirectCallback and navigate to avoid recreating the listener
	}, [setMobileAuthReady]);

	return null; // This component doesn't render anything
}

// Component to set up API configuration
function ApiConfigHandler() {
	const { getAccessTokenSilently, logout } = useAuth0();
	const { setLoading } = useGlobalLoading();
	const analytics = useAnalytics();
	const analyticsRef = useRef(analytics);
	analyticsRef.current = analytics;

	useEffect(() => {
		// Set up global 401 handler for API calls
		// Made async to properly await performLogout and prevent race conditions
		setApiUnauthorizedHandler(async () => {
			// Log out the user to clear Auth0 session and prevent infinite loops
			console.log("❌ [APP] Authentication failed - logging out user");
			analyticsRef.current.logAuth("logout", {
				trigger: "session_expired",
				platform: Capacitor.getPlatform(),
			});
			await performLogout(logout);
		});

		// Set up global token provider for API calls
		setApiTokenProvider(() =>
			getAccessTokenSilently({
				authorizationParams: {
					audience: import.meta.env.VITE_AUTH0_AUDIENCE,
				},
			}),
		);

		// Set up global loading handler for API calls
		setApiLoadingHandler((loading: boolean, message?: string) => {
			setLoading(loading, message);
		});
	}, [getAccessTokenSilently, logout, setLoading]);

	return null;
}

// Component to initialize RevenueCat SDK
function RevenueCatConfigHandler() {
	useEffect(() => {
		// Initialize RevenueCat on all platforms (iOS, Android, Web)
		// Web purchases work via Stripe integration
		// User ID will be set later in UserDataLoader with database user ID
		RevenueCatService.configure().catch((error) => {
			console.error("Failed to initialize RevenueCat:", error);
		});
	}, []);

	return null;
}

// Component to configure StatusBar for mobile
function StatusBarConfigHandler() {
	useEffect(() => {
		const configureStatusBar = async () => {
			try {
				const platform = (
					await import("@capacitor/core")
				).Capacitor.getPlatform();

				if (platform === "ios") {
					// iOS: Use non-overlay mode - works well
					await StatusBar.setOverlaysWebView({ overlay: false });
					await StatusBar.setStyle({ style: Style.Light });
					await StatusBar.setBackgroundColor({ color: "#ffffff" });
					console.log("✅ iOS StatusBar configured: non-overlay mode");
				} else if (platform === "android") {
					// Android 15+: Edge-to-edge is enforced, overlay: false doesn't work reliably
					// Use overlay mode with transparent status bar + CSS safe-area padding
					await StatusBar.setOverlaysWebView({ overlay: true });
					await StatusBar.setStyle({ style: Style.Light });
					await StatusBar.setBackgroundColor({ color: "#ffffff" });

					// Add class to body so CSS can apply safe-area padding
					document.body.classList.add("android-platform");
					console.log(
						"✅ Android StatusBar configured: overlay mode with CSS safe-area",
					);
				}
			} catch (_error) {
				// StatusBar API is only available on mobile platforms
				// Silently ignore errors on web
				console.log("ℹ️ StatusBar not available (likely web platform)");
			}
		};

		configureStatusBar();
	}, []);

	return null;
}

// Wrapper to conditionally render Statsig only after authentication
function StatsigWrapper({ children }: { children: React.ReactNode }) {
	const { isAuthenticated, user } = useAuth0();

	// Only initialize Statsig after user is authenticated
	if (!isAuthenticated || !user?.sub) {
		return <>{children}</>;
	}

	return (
		<EraStatsigProvider
			userId={user.sub}
			userEmail={user.email}
			userName={user.name}
			userPicture={user.picture}
		>
			{children}
		</EraStatsigProvider>
	);
}

// Component to handle app lifecycle events (resume, pause) and proactive token refresh
function AppLifecycleHandler() {
	const analytics = useAnalytics();
	const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
	const queryClient = useQueryClient();
	const { triggerAppResume } = useMobileAuth();
	const hasTrackedResume = useRef(false);

	useEffect(() => {
		// Only track on native platforms
		if (!Capacitor.isNativePlatform()) return;

		const setupListener = async () => {
			const listener = await CapApp.addListener(
				"appStateChange",
				async ({ isActive }) => {
					if (isActive && !hasTrackedResume.current) {
						// App resumed from background
						hasTrackedResume.current = true;
						console.log("📱 App resumed from background");

						// Log analytics event
						analytics.logUserAction("app_resume", {
							platform: Capacitor.getPlatform(),
							is_authenticated: String(isAuthenticated),
							user_id: user?.sub || "anonymous",
						});

						// Trigger app resume event to reset SessionGuard validation
						// This ensures fresh auth checks after the app has been backgrounded
						triggerAppResume();

						// Proactively refresh token when app resumes (if authenticated)
						// This prevents stale token issues after long background periods
						if (isAuthenticated) {
							console.log("🔄 Proactively refreshing token on app resume...");
							try {
								await getAccessTokenSilently({
									authorizationParams: {
										audience: import.meta.env.VITE_AUTH0_AUDIENCE,
									},
									cacheMode: "off", // Force refresh, don't use cached token
								});
								console.log("✅ Token refreshed successfully on app resume");

								// Invalidate React Query cache to refetch data with fresh token
								await queryClient.invalidateQueries();
								console.log("✅ React Query cache invalidated");

								analytics.logUserAction("token_refresh_on_resume", {
									status: "success",
								});
							} catch (error) {
								console.error("❌ Token refresh failed on app resume:", error);
								analytics.logUserAction("token_refresh_on_resume", {
									status: "failed",
									error: error instanceof Error ? error.message : "unknown",
								});
								// Don't logout here - let the API client handle it
								// The API interceptor will trigger logout on 401 responses
								// With deduplication in performLogout, multiple API failures
								// won't cause multiple logouts anymore
							}
						}

						// Reset after a short delay to allow tracking next resume
						setTimeout(() => {
							hasTrackedResume.current = false;
						}, 1000);
					}
				},
			);

			return listener;
		};

		let listenerCleanup: { remove: () => void } | undefined;
		setupListener().then((listener) => {
			listenerCleanup = listener;
		});

		return () => {
			if (listenerCleanup) {
				listenerCleanup.remove();
			}
		};
	}, [
		analytics,
		isAuthenticated,
		user?.sub,
		getAccessTokenSilently,
		queryClient,
		triggerAppResume,
	]);

	return null;
}

function App() {
	return (
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<GlobalLoadingProvider>
					<MobileAuthProvider>
						<Router>
							<AuthProvider>
								<DemoSettingsProvider>
									<LanguageProvider>
										<PrimeReactConfigProvider>
											<MobileCallbackHandler />
											<AppLifecycleHandler />
											<SessionTimeoutHandler />
											<ApiConfigHandler />
											<RevenueCatConfigHandler />
											<StatusBarConfigHandler />
											<SentryUserProvider>
												<StatsigWrapper>
													<CustomAblyProvider>
														<UserDataProvider initialUserData={null}>
															<ChatActionsProvider>
																<ModalProvider>
																	<UserDataLoader />
																	<Toaster position="top-center" />
																	<Routes>
																		<Route
																			path="/"
																			element={
																				<SessionGuard requireAuth={true}>
																					<HomePage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/invitation/:token"
																			element={<InvitationAccept />}
																		/>
																		<Route
																			path="/login"
																			element={
																				<SessionGuard requireAuth={false}>
																					<Login />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/callback"
																			element={
																				<SessionGuard requireAuth={true}>
																					<Callback />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/2fa"
																			element={
																				<SessionGuard requireAuth={true}>
																					<TwoFactor />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/onboarding"
																			element={
																				<SessionGuard requireAuth={true}>
																					<Onboarding />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/onboarding/thank-you"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ThankYou />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/chat/:id"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ChatPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/profile"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ProfilePage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/settings"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SettingsPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/settings/subscription"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SubscriptionSettings />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/settings/security"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SecuritySettings />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/settings/support"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SupportSettings />
																				</SessionGuard>
																			}
																		/>
																		{/* Legacy redirects: /profile/* → /settings/* */}
																		<Route
																			path="/profile/subscription"
																			element={
																				<Navigate
																					to="/settings/subscription"
																					replace
																				/>
																			}
																		/>
																		<Route
																			path="/profile/security"
																			element={
																				<Navigate
																					to="/settings/security"
																					replace
																				/>
																			}
																		/>
																		<Route
																			path="/profile/support"
																			element={
																				<Navigate
																					to="/settings/support"
																					replace
																				/>
																			}
																		/>
																		<Route
																			path="/documents"
																			element={
																				<SessionGuard requireAuth={true}>
																					<Documents />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/chat-history"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ChatHistory />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/communication"
																			element={
																				<SessionGuard requireAuth={true}>
																					<Communication />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/education"
																			element={
																				<SessionGuard requireAuth={true}>
																					<EducationHub />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/journey"
																			element={
																				<SessionGuard requireAuth={true}>
																					<Journey />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/chat-with-provider"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ChatWithProvider />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/meet-expert"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ConsultExpert />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/pricing"
																			element={
																				<SessionGuard requireAuth={true}>
																					<Pricing />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/files"
																			element={
																				<SessionGuard requireAuth={true}>
																					<FilesPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready"
																			element={
																				<SessionGuard requireAuth={true}>
																					<GetReadyPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready/tasks/book-appointment"
																			element={
																				<SessionGuard requireAuth={true}>
																					<BookAppointmentPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready/tasks/:taskId/book-appointment"
																			element={
																				<SessionGuard requireAuth={true}>
																					<BookAppointmentPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready/tasks/upload-documents"
																			element={
																				<SessionGuard requireAuth={true}>
																					<UploadDocumentsPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready/tasks/:taskId/upload-documents"
																			element={
																				<SessionGuard requireAuth={true}>
																					<UploadDocumentsPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready/tasks/sign-consent"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SignConsentPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/get-ready/tasks/:taskId/sign-consent"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SignConsentPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/track"
																			element={
																				<SessionGuard requireAuth={true}>
																					<TrackPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/share"
																			element={
																				<SessionGuard requireAuth={true}>
																					<SharePage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/questionnaire"
																			element={
																				<SessionGuard requireAuth={true}>
																					<QuestionnairePage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="/chat"
																			element={
																				<SessionGuard requireAuth={true}>
																					<ChatPage />
																				</SessionGuard>
																			}
																		/>
																		<Route
																			path="*"
																			element={<Navigate to="/login" replace />}
																		/>
																	</Routes>
																	<BottomNav />
																</ModalProvider>
															</ChatActionsProvider>
														</UserDataProvider>
													</CustomAblyProvider>
												</StatsigWrapper>
											</SentryUserProvider>
										</PrimeReactConfigProvider>
									</LanguageProvider>
								</DemoSettingsProvider>
							</AuthProvider>
						</Router>
					</MobileAuthProvider>
					<GlobalLoadingModal />
				</GlobalLoadingProvider>
				<Analytics />
			</QueryClientProvider>
		</ErrorBoundary>
	);
}

export default App;
